import { BULLETED_LINE, NUMBERED_LINE, QUOTE_LINE } from "./waFormat";

/**
 * The composer's formatting, as on WhatsApp Web (measured there, and its help center's "Como
 * formatar suas mensagens"): the marks go into the text itself, so what the contact receives is
 * formatted by WhatsApp. Each function returns the one edit to make (the text from `from` to `to`
 * becomes `insert`) and the selection after it, so the browser's undo takes it as one step.
 */
export interface TextEdit {
  from: number;
  to: number;
  insert: string;
  selStart: number;
  selEnd: number;
}

export type InlineFormat = "bold" | "italic" | "strike" | "code";
export type LineFormat = "numbered" | "bulleted" | "quote";
export type Format = InlineFormat | LineFormat;

export const INLINE_MARKS: Record<InlineFormat, string> = {
  bold: "*",
  italic: "_",
  strike: "~",
  code: "`",
};

const lineStart = (value: string, at: number) =>
  value.lastIndexOf("\n", at - 1) + 1;
const lineEnd = (value: string, at: number) => {
  const end = value.indexOf("\n", at);
  return end === -1 ? value.length : end;
};

/**
 * A new line inside a list (Shift+Enter, or Enter when Enter does not send) carries the list on:
 * "2. " after "1. ", the same bullet after "- " or "* ". On an item left empty it ends the list
 * instead, taking the marker away. Quotes are not carried on. Null: an ordinary new line.
 */
export function continueList(
  value: string,
  start: number,
  end: number,
): TextEdit | null {
  if (start !== end) return null;
  const from = lineStart(value, start);
  const line = value.slice(from, lineEnd(value, start));
  const numbered = NUMBERED_LINE.exec(line);
  const bulleted = numbered ? null : BULLETED_LINE.exec(line);
  const marker = numbered
    ? `${numbered[1]}. `
    : bulleted
      ? `${bulleted[1]} `
      : null;
  if (!marker || start < from + marker.length) return null;
  if (line === marker)
    return {
      from,
      to: from + line.length,
      insert: "",
      selStart: from,
      selEnd: from,
    };
  const next = numbered ? `${Math.min(Number(numbered[1]) + 1, 99)}. ` : marker;
  const insert = "\n" + next;
  return {
    from: start,
    to: start,
    insert,
    selStart: start + insert.length,
    selEnd: start + insert.length,
  };
}

/**
 * Bold, italic, strikethrough or inline code on the selection: the marks go around it, without
 * the spaces at its edges (a mark next to a space does not count). On text already marked, the
 * marks come off. With nothing selected, the pair goes in with the cursor between them.
 */
export function toggleInline(
  value: string,
  start: number,
  end: number,
  format: InlineFormat,
): TextEdit {
  const mark = INLINE_MARKS[format];
  const m = mark.length;
  let a = start;
  let b = end;
  while (a < b && /\s/.test(value[a]!)) a++;
  while (b > a && /\s/.test(value[b - 1]!)) b--;
  if (a === b)
    return {
      from: start,
      to: end,
      insert: mark + mark,
      selStart: start + m,
      selEnd: start + m,
    };
  const inner = value.slice(a, b);
  if (value.slice(a - m, a) === mark && value.slice(b, b + m) === mark)
    return {
      from: a - m,
      to: b + m,
      insert: inner,
      selStart: a - m,
      selEnd: b - m,
    };
  if (inner.length > 2 * m && inner.startsWith(mark) && inner.endsWith(mark)) {
    const bare = inner.slice(m, -m);
    return {
      from: a,
      to: b,
      insert: bare,
      selStart: a,
      selEnd: a + bare.length,
    };
  }
  return {
    from: a,
    to: b,
    insert: mark + inner + mark,
    selStart: a + m,
    selEnd: b + m,
  };
}

const PREFIX = /^(?:\d{1,2}\. |[-*] |> )/;
const hasLine = (line: string, format: LineFormat) =>
  format === "numbered"
    ? NUMBERED_LINE.test(line)
    : format === "bulleted"
      ? BULLETED_LINE.test(line)
      : QUOTE_LINE.test(line);

/**
 * A numbered list, a bulleted list or a quote on every line the selection touches: each line gets
 * the marker ("1. ", "2. "…, "- " or "> "), replacing another list's or a quote's. When they all
 * have it already, it comes off.
 */
export function toggleLines(
  value: string,
  start: number,
  end: number,
  format: LineFormat,
): TextEdit {
  const from = lineStart(value, start);
  const to = lineEnd(value, Math.max(start, end - (end > start ? 1 : 0)));
  const lines = value.slice(from, to).split("\n");
  const off = lines.every((l) => hasLine(l, format));
  const out = lines.map((l, i) => {
    const bare = l.replace(PREFIX, "");
    if (off) return bare;
    return (
      (format === "numbered"
        ? `${i + 1}. `
        : format === "bulleted"
          ? "- "
          : "> ") + bare
    );
  });
  const insert = out.join("\n");
  return {
    from,
    to,
    insert,
    selStart: from,
    selEnd: from + insert.length,
  };
}

/** What the selection already has, lit in the bar as WhatsApp Web does. */
export function activeFormats(
  value: string,
  start: number,
  end: number,
): Format[] {
  const out: Format[] = [];
  let a = start;
  let b = end;
  while (a < b && /\s/.test(value[a]!)) a++;
  while (b > a && /\s/.test(value[b - 1]!)) b--;
  for (const [format, mark] of Object.entries(INLINE_MARKS) as Array<
    [InlineFormat, string]
  >) {
    const m = mark.length;
    if (
      a < b &&
      value.slice(a - m, a) === mark &&
      value.slice(b, b + m) === mark
    )
      out.push(format);
  }
  const lines = value
    .slice(
      lineStart(value, start),
      lineEnd(value, Math.max(start, end - (end > start ? 1 : 0))),
    )
    .split("\n");
  for (const format of ["numbered", "bulleted", "quote"] as LineFormat[])
    if (lines.every((l) => hasLine(l, format))) out.push(format);
  return out;
}
